import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styles from "./Schoolsetup.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomFileInput,
  CustomTextInput,
} from "../../../components";
import AxiosInstance, { TokenService } from "../../../services/Api";
import BlobImage from "../../../components/BlobImage/BlobImage";
import { validateSchoolSetupForm } from "./Validation";
import PopupModal from "../../../components/PopupModal/PopupModal";
import {
  fetchCountryOptions,
  fetchStateOptions,
  STATE_TYPES,
} from "../../../redux/Masterdata";

const SchoolSetUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { CLIENTID } = location.state || {};
  const { id } = useParams();
  const user = TokenService.getUser();
  const userId = user?.id || 0;

  // School cockpit users open /user/school-setup WITHOUT an :id —
  // fall back to their own school from the login cookie so the
  // existing school data loads and Save updates (not creates).
  const ownSchoolId = user?.client_data?.[0]?.school_id || "";
  const effectiveSchoolId = id || ownSchoolId;

  // ─── Redux Selectors (Country & State from API) ───
  const {
    countryOptions: reduxCountryOptions,
    fatherStateOptions: reduxFatherStateOptions,
  } = useSelector((state) => state.masterData);

  const countryOptions = useMemo(
    () => [
      {
        value: "",
        label: "Select Country",
      },
      ...reduxCountryOptions,
    ],
    [reduxCountryOptions],
  );

  const stateOptions = useMemo(
    () => [
      {
        value: "",
        label: "Select State",
      },
      ...reduxFatherStateOptions,
    ],
    [reduxFatherStateOptions],
  );

  // ─── Modal State ───
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  // ─── Form State ───
  const [formData, setFormData] = useState({
    schoolId: null,
    schoolName: "",
    schoolShortName: "",
    emailAddress: "",
    websiteAddress: "",
    address1: "",
    address2: "",
    address3: "",
    city: "",
    state: null,
    country: null,
    pincode: "",
    mobileNumber: "",
    schoolMobileNumber: "",
    fax: "",
    landlineNo: "",
    schoolCategory: null,
    boardAffiliation: null,
    approvingAuthority: null,
    mediumOfStudy: null,
    schoolLogo: null,
    principalFirstName: "",
    principalMiddleName: "",
    principalLastName: "",
    principalEmail: "",
    principalMobile: "",
    adminFirstName: "",
    adminMiddleName: "",
    adminLastName: "",
    adminEmail: "",
    adminMobile: "",
    systemAdmin: null,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [apiSchoolData, setApiSchoolData] = useState(null);
  // ─── Static Dropdown Options ───
  const schoolCategoryOptions = [
    { value: "116", label: "Primary School (Class I to V)" },
    { value: "117", label: "Middle School (Class VI to VIII)" },
    { value: "118", label: "High School (Class IX to X)" },
    { value: "119", label: "Higher Secondary School (Class XI to XII)" },
    { value: "120", label: "Senior Secondary School" },
  ];

  const boardAffiliationOptions = [
    { value: "1", label: "Central Board of Secondary Education (CBSE)" },
    { value: "2", label: "State Government Board of Education" },
    { value: "3", label: "Indian Certificate of Secondary Education (ICSE)" },
    { value: "4", label: "International Baccalaureate (IB)" },
    { value: "5", label: "Cambridge International" },
  ];

  const approvingAuthorityOptions = [
    { value: "1", label: "State Board" },
    { value: "2", label: "CBSE" },
    { value: "3", label: "ICSE" },
    { value: "4", label: "IB" },
    { value: "5", label: "Cambridge" },
  ];

  const mediumOfStudyOptions = [
    { value: "1", label: "Tamil" },
    { value: "2", label: "English" },
    { value: "3", label: "Hindi" },
    { value: "4", label: "Telugu" },
    { value: "5", label: "Kannada" },
    { value: "6", label: "Malayalam" },
  ];

  // ─── Fetch Country Options on Mount ───
  useEffect(() => {
    dispatch(fetchCountryOptions());
  }, [dispatch]);

  // ─── Fetch State Options when Country Changes ───
  useEffect(() => {
    if (formData.country?.value) {
      dispatch(
        fetchStateOptions({
          countryId: formData.country.value,
          stateType: STATE_TYPES.FATHER,
        }),
      );
    }
  }, [formData.country, dispatch]);

  // ─── Fetch School Data on Mount if ID Exists ───
  // (URL :id for group cockpit edit, or own school for school cockpit)
  useEffect(() => {
    if (effectiveSchoolId) {
      fetchSchoolData(effectiveSchoolId);
    }
  }, [effectiveSchoolId]);
  useEffect(() => {
    if (apiSchoolData && countryOptions.length > 1) {
      mapApiDataToForm(apiSchoolData);
    }
  }, [apiSchoolData, countryOptions]);
  // ─── Fetch School Data from API ───
  const fetchSchoolData = async (schoolId) => {
    setFetchLoading(true);
    try {
      const response = await AxiosInstance.get(
        `groupCockpit/school-setup/${schoolId}`,
      );

      if (response?.data?.success) {
        const data = response.data.data;
        setIsEditMode(true);
        setApiSchoolData(data);
      }
    } catch (error) {
      console.error("Error fetching school data:", error);
      setModalState({
        open: true,
        type: "error",
        message: "Failed to fetch school data. Please try again.",
        redirectPath: "",
      });
    } finally {
      setFetchLoading(false);
    }
  };
  useEffect(() => {
    if (apiSchoolData && stateOptions.length > 1) {
      const stateOption = stateOptions.find(
        (opt) => String(opt.value) === String(apiSchoolData.state)
      );

      if (stateOption) {
        setFormData((prev) => ({
          ...prev,
          state: stateOption,
        }));
      }
    }
  }, [stateOptions, apiSchoolData]);
  // ─── Map API Response to Form Data ───
  const mapApiDataToForm = (data) => {
    const stateOption = stateOptions.find(
      (opt) => String(opt.value) === String(data.state)
    );

    const countryOption = countryOptions.find(
      (opt) => String(opt.value) === String(data.country)
    );
    const categoryOption = schoolCategoryOptions.find(
      (opt) => opt.value === String(data.school_category),
    );
    const boardOption = boardAffiliationOptions.find(
      (opt) =>
        opt.label === data.school_board ||
        opt.value === String(data.school_board),
    );
    const approvedByOption = approvingAuthorityOptions.find(
      (opt) =>
        opt.label === data.school_approved_by ||
        opt.value === String(data.school_approved_by),
    );
    const mediumOption = mediumOfStudyOptions.find(
      (opt) =>
        opt.label === data.medium_of_school ||
        opt.value === String(data.medium_of_school),
    );

    setFormData({
      schoolId: data.school_id || null,
      schoolName: data.school_name || "",
      schoolShortName: data.short_school_name || "",
      emailAddress: data.school_email || "",
      websiteAddress: data.web_address || "",
      address1: data.address1 || "",
      address2: data.address2 || "",
      address3: data.address3 || "",
      city: data.city || data.city_name || "",
      state:  null,
      country: countryOption || null,
      pincode: data.postal_no ? String(data.postal_no) : "",
      mobileNumber: data.mobile_no ? String(data.mobile_no) : "",
      schoolMobileNumber: data.school_mobile_no
        ? String(data.school_mobile_no)
        : "",
      fax: data.fax || "",
      landlineNo: data.landline_no || "",
      schoolCategory: categoryOption || null,
      boardAffiliation: boardOption || null,
      approvingAuthority: approvedByOption || null,
      mediumOfStudy: mediumOption || null,
      schoolLogo: data.school_logo || null,
      principalFirstName: data.principal_first_name || "",
      principalMiddleName: data.principal_middle_name || "",
      principalLastName: data.principal_last_name || "",
      principalEmail: data.principal_email || "",
      principalMobile: data.principal_mobile
        ? String(data.principal_mobile)
        : "",
      adminFirstName:
        data.first_name || data.admin?.first_name || data.principal_first_name || "",
      adminMiddleName:
        data.middle_name || data.admin?.middle_name || data.principal_middle_name || "",
      adminLastName:
        data.last_name || data.admin?.last_name || data.principal_last_name || "",
      // The administrator's OWN email lives in principal_email; the
      // login user's email (data.email) may be a different person
      // (school/organizer login) — show the administrator's email
      // when the administrator is a different person.
      adminEmail: data.principal_email || data.email || data.user?.email || "",
      adminMobile: data.principal_mobile
        ? String(data.principal_mobile)
        : data.school_mobile_no
          ? String(data.school_mobile_no)
          : data.admin?.school_mobile_no
            ? String(data.admin.school_mobile_no)
            : "",
      systemAdmin: data.system_admin || null,
    });
  };

  // ─── Fields that only allow digits (no alphabets, no special chars) ───
  const numericOnlyFields = [
    "mobileNumber",
    "schoolMobileNumber",
    "principalMobile",
    "adminMobile",
    "pincode",
    "fax",
    "landlineNo",
  ];

  // ─── Input Change Handler ───
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    // Allow only digits for all numeric fields
    if (numericOnlyFields.includes(name)) {
      updatedValue = value.replace(/\D/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // ─── Dropdown Change Handler ───
  const handleDropdownChange = (name, option) => {
    // Reset state when country changes
    if (name === "country") {
      setFormData((prev) => ({
        ...prev,
        country: option,
        state: null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: option,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // ─── File Change Handler ───
  const handleFileChange = useCallback((file, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: file,
    }));
  }, []);

  // ─── Prepare Payload for API ───
  const preparePayload = () => {
    const payload = {
      id: CLIENTID,
      created_by: userId,
      school_name: formData.schoolName,
      short_school_name: formData.schoolShortName,
      school_email: formData.emailAddress,
      web_address: formData.websiteAddress,
      address1: formData.address1,
      address2: formData.address2 || null,
      address3: formData.address3 || null,
      city: formData.city,
      state: formData.state?.value || null,
      country: formData.country?.value || null,
      postal_no: formData.pincode,
      mobile_no: formData.mobileNumber,
      school_mobile_no: formData.schoolMobileNumber || formData.adminMobile,
      landline_no: formData.landlineNo || null,
      fax: formData.fax || null,
      school_category: formData.schoolCategory?.value || null,
      school_board: formData.boardAffiliation?.value || null,
      school_approved_by: formData.approvingAuthority?.value || null,
      medium_of_school: formData.mediumOfStudy?.value
        ? [formData.mediumOfStudy.value]
        : [],
      // single "School Administrator" contact block — the same person
      // is stored as both principal and admin
      principal_first_name: formData.adminFirstName,
      principal_middle_name: formData.adminMiddleName || null,
      principal_last_name: formData.adminLastName,
      principal_email: formData.adminEmail,
      principal_mobile: formData.adminMobile,
      first_name: formData.adminFirstName,
      middle_name: formData.adminMiddleName || null,
      last_name: formData.adminLastName,
      email: formData.adminEmail,
      system_admin: "1",
    };

    return payload;
  };

  // ─── Save Handler ───
  const handleSave = async () => {
    // single School Administrator block — mirror its values into the
    // principal fields so the shared validation passes unchanged
    const mergedForm = {
      ...formData,
      principalFirstName: formData.adminFirstName,
      principalMiddleName: formData.adminMiddleName,
      principalLastName: formData.adminLastName,
      principalEmail: formData.adminEmail,
      principalMobile: formData.adminMobile,
    };
    const { errors, isValid } = validateSchoolSetupForm(mergedForm, false);

    if (!isValid) {
      setErrors(errors);
      return;
    }

    setLoading(true);

    try {
      const payload = preparePayload();

      let finalPayload = payload;
      if (formData.schoolLogo instanceof File) {
        const formDataObj = new FormData();
        Object.keys(payload).forEach((key) => {
          if (payload[key] !== null && payload[key] !== undefined) {
            if (Array.isArray(payload[key])) {
              payload[key].forEach((item, index) => {
                formDataObj.append(`${key}[${index}]`, item);
              });
            } else {
              formDataObj.append(key, payload[key]);
            }
          }
        });
        formDataObj.append("school_logo", formData.schoolLogo);
        finalPayload = formDataObj;
      }

      let response;
      if (isEditMode) {
        response = await AxiosInstance.put(
          `groupCockpit/school-setup/${effectiveSchoolId}`,
          finalPayload,
          formData.schoolLogo instanceof File
            ? { headers: { "Content-Type": "multipart/form-data" } }
            : {},
        );
      } else {
        response = await AxiosInstance.post(
          "groupCockpit/school_setup",
          finalPayload,
          formData.schoolLogo instanceof File
            ? { headers: { "Content-Type": "multipart/form-data" } }
            : {},
        );
      }

      if (response?.data?.success === true) {
        const successMessage =
          response.data.message ||
          (isEditMode
            ? "School updated successfully!"
            : "School created successfully!");

        setModalState({
          open: true,
          type: "success",
          message: successMessage,
          redirectPath: "/user/dashboard",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || "Failed to save school data.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error saving school data:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          "Failed to save school data. Please try again.",
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─── Cancel Handler ───
  const handleCancel = () => {
    navigate("/user/dashboard");
  };

  // ─── Modal OK Handler ───
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  // ─── Loading State ───
  if (fetchLoading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.formCard}>
          <div className={styles.loadingContainer}>
            <i className="fa fa-spinner fa-spin"></i>
            <span>Loading school data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.pageContainer}>
        <div className={styles.formCard}>
          {/* ── School Setup Section ── */}
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <i className="fa fa-school"></i>
              <h2 className={styles.sectionTitle}>School Setup</h2>
            </div>

            <div className={styles.formGrid}>
              <CustomTextInput
                label="School Name"
                name="schoolName"
                value={formData.schoolName}
                onChange={handleInputChange}
                placeholder="E.g. Best School"
                required
                error={errors.schoolName}
              />
              <CustomTextInput
                label="School Short Name"
                name="schoolShortName"
                value={formData.schoolShortName}
                onChange={handleInputChange}
                placeholder="E.g. Best School"
                required
                error={errors.schoolShortName}
              />
              <CustomTextInput
                label="Email Address"
                name="emailAddress"
                type="email"
                value={formData.emailAddress}
                onChange={handleInputChange}
                placeholder="E.g. bestschool@gmail.com"
                required
                error={errors.emailAddress}
              />
            </div>

            <div className={styles.formGrid}>
              <CustomTextInput
                label="Website Address"
                name="websiteAddress"
                value={formData.websiteAddress}
                onChange={handleInputChange}
                placeholder="E.g. https://www.BestSchool.com"
                required
                error={errors.websiteAddress}
              />
              <CustomTextInput
                label="Address 1"
                name="address1"
                value={formData.address1}
                onChange={handleInputChange}
                placeholder="E.g. 23/12, 2nd Street, Anna Nagar, Chennai"
                required
                error={errors.address1}
              />
              <CustomTextInput
                label="Address 2"
                name="address2"
                value={formData.address2}
                onChange={handleInputChange}
                placeholder="E.g. 23/12, 2nd Street, Anna Nagar, Chennai"
              />
            </div>

            <div className={styles.formGrid}>
              <CustomTextInput
                label="Address 3"
                name="address3"
                value={formData.address3}
                onChange={handleInputChange}
                placeholder="E.g. 23/12, 2nd Street, Anna Nagar, Chennai"
              />
              <CustomTextInput
                label="City"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="E.g. Chennai"
                required
                error={errors.city}
              />
              <CustomDropdown
                label="Country"
                name="country"
                value={formData.country}
                onChange={(option) => handleDropdownChange("country", option)}
                options={countryOptions}
                placeholder="Select Country"
                required
                error={errors.country}
              />
            </div>

            <div className={styles.formGrid}>
              <CustomDropdown
                label="State"
                name="state"
                value={formData.state}
                onChange={(option) => handleDropdownChange("state", option)}
                options={stateOptions}
                placeholder="Select State"
                required
                isDisabled={!formData.country}
                error={errors.state}
              />
              <CustomTextInput
                label="Pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                placeholder="E.g. 612345"
                required
                maxLength={6}
                error={errors.pincode}
              />
              <CustomTextInput
                label="Mobile Number"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleInputChange}
                placeholder="E.g. 9876543210"
                required
                maxLength={10}
                error={errors.mobileNumber}
              />
            </div>

            <div className={styles.formGrid}>
              <CustomTextInput
                label="School Mobile Number"
                name="schoolMobileNumber"
                value={formData.schoolMobileNumber}
                onChange={handleInputChange}
                placeholder="E.g. 9876543210"
                maxLength={10}
                error={errors.schoolMobileNumber}
              />
              <CustomTextInput
                label="Fax"
                name="fax"
                value={formData.fax}
                onChange={handleInputChange}
                placeholder="E.g. 612345"
              />
              <CustomTextInput
                label="Landline Number"
                name="landlineNo"
                value={formData.landlineNo}
                onChange={handleInputChange}
                placeholder="E.g. 04412345678"
                maxLength={15}
              />
            </div>

            <div className={styles.formGrid}>
              <CustomDropdown
                label="School Category"
                name="schoolCategory"
                value={formData.schoolCategory}
                onChange={(option) =>
                  handleDropdownChange("schoolCategory", option)
                }
                options={schoolCategoryOptions}
                placeholder="E.g. Higher secondary school"
                required
                error={errors.schoolCategory}
              />
              <CustomDropdown
                label="Board Affiliation"
                name="boardAffiliation"
                value={formData.boardAffiliation}
                onChange={(option) =>
                  handleDropdownChange("boardAffiliation", option)
                }
                options={boardAffiliationOptions}
                placeholder="E.g. Central"
                required
                error={errors.boardAffiliation}
              />
              <CustomDropdown
                label="Approving Authority"
                name="approvingAuthority"
                value={formData.approvingAuthority}
                onChange={(option) =>
                  handleDropdownChange("approvingAuthority", option)
                }
                options={approvingAuthorityOptions}
                placeholder="E.g. CBSE"
                required
                error={errors.approvingAuthority}
              />
            </div>

            <div className={styles.formGrid}>
              <CustomDropdown
                label="Medium of Study"
                name="mediumOfStudy"
                value={formData.mediumOfStudy}
                onChange={(option) =>
                  handleDropdownChange("mediumOfStudy", option)
                }
                options={mediumOfStudyOptions}
                placeholder="E.g. Tamil"
                required
                error={errors.mediumOfStudy}
              />

              {/* School Logo (input + preview) — right of Medium of Study */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "16px",
                  flexWrap: "wrap",
                  gridColumn: "span 2",
                }}
              >
                <div style={{ flex: "0 1 320px", minWidth: "260px" }}>
                  <CustomFileInput
                    label="School Logo"
                    name="schoolLogo"
                    value={formData.schoolLogo}
                    onChange={handleFileChange}
                    accept="image/*"
                    placeholder={
                      typeof formData.schoolLogo === "string" &&
                      formData.schoolLogo
                        ? formData.schoolLogo.split("/").pop()
                        : "No file chosen"
                    }
                    buttonText="Choose File"
                  />
                </div>
                {/* Logo preview — newly chosen file, or the stored logo (blob-aware) */}
                {formData.schoolLogo instanceof File ? (
                  <img
                    src={URL.createObjectURL(formData.schoolLogo)}
                    alt="School Logo Preview"
                    style={{
                      width: "96px",
                      height: "96px",
                      objectFit: "contain",
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      padding: "4px",
                      background: "#fff",
                    }}
                  />
                ) : typeof formData.schoolLogo === "string" &&
                  formData.schoolLogo ? (
                  <BlobImage
                    path={formData.schoolLogo}
                    alt="School Logo"
                    style={{
                      width: "96px",
                      height: "96px",
                      objectFit: "contain",
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                      padding: "4px",
                      background: "#fff",
                    }}
                  />
                ) : null}
              </div>
            </div>
          </div>

          {/* ── Contact Details Section ── */}
          <div className={styles.section}>
            {/* <div className={styles.sectionHeader}>
              <i className="fa fa-address-book"></i>
              <h2 className={styles.sectionTitle}>Contact Details</h2>
            </div> */}

            {/* School Administrator Subsection (single contact block) */}
            <div className={styles.subsection}>
              <div className={styles.subsectionHeader}>
                <i className="fa fa-user-friends"></i>
                <h3 className={styles.subsectionTitle}>School Administrator</h3>
              </div>

              <div className={styles.formGrid}>
                <CustomTextInput
                  label="First Name"
                  name="adminFirstName"
                  value={formData.adminFirstName}
                  onChange={handleInputChange}
                  placeholder="E.g. Mary"
                  required
                  error={errors.adminFirstName}
                />
                <CustomTextInput
                  label="Middle Name"
                  name="adminMiddleName"
                  value={formData.adminMiddleName}
                  onChange={handleInputChange}
                  placeholder="E.g. Elizabeth"
                />
                <CustomTextInput
                  label="Last Name"
                  name="adminLastName"
                  value={formData.adminLastName}
                  onChange={handleInputChange}
                  placeholder="E.g. Smith"
                  required
                  error={errors.adminLastName}
                />
              </div>

              <div className={styles.formGridTwo}>
                <CustomTextInput
                  label="Email Address"
                  name="adminEmail"
                  type="email"
                  value={formData.adminEmail}
                  onChange={handleInputChange}
                  placeholder="E.g. Mary@gmail.com"
                  required
                  error={errors.adminEmail}
                />
                <CustomTextInput
                  label="Mobile Number"
                  name="adminMobile"
                  value={formData.adminMobile}
                  onChange={handleInputChange}
                  placeholder="E.g. 9876543210"
                  required
                  maxLength={10}
                  error={errors.adminMobile}
                />
              </div>
            </div>
          </div>

          {/* ── Action Buttons ── */}
          <div className={styles.actionButtons}>
            <CustomButton
              variant="contained"
              onClick={handleSave}
              className={styles.saveButton}
              fullWidth={false}
              loading={loading}
              loadingText="Saving..."
            >
              {isEditMode ? "UPDATE" : "SAVE"}
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={handleCancel}
              className={styles.cancelButton}
              fullWidth={false}
              disabled={loading}
            >
              CANCEL
            </CustomButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default SchoolSetUp;
